import * as Icons from '@/lib/icons';

export interface ProcessStep {
  label: string;
  icon: string;
}

interface Props {
  steps: ProcessStep[];
  // activeStep optionally highlights only nodes up to a given index; when
  // omitted (the default) every node renders highlighted so the full flow is
  // visible at once.
  activeStep?: number;
  className?: string;
}

type IconRecord = Record<string, unknown>;
type IconComponent = (props: { size?: number; className?: string }) => JSX.Element;

function resolveIcon(name: string): IconComponent | null {
  const record = Icons as unknown as IconRecord;
  const candidate = record[name];
  if (typeof candidate === 'function') return candidate as IconComponent;
  return null;
}

// ProcessDiagram renders a row of labelled nodes connected by lines as a static
// flow chart: the whole sequence is shown at once (numbered + connected) with
// no auto-playing highlight. Order is conveyed by the layout and the "Step N"
// labels, not by motion.
export function ProcessDiagram({ steps, activeStep, className }: Props): JSX.Element {
  const isActive = (i: number): boolean => (activeStep === undefined ? true : i <= activeStep);

  return (
    <div
      className={
        'rounded-lg border border-dash-border bg-dash-surface/40 p-4 ' + (className ?? '')
      }
    >
      <ol className="flex flex-wrap items-stretch gap-3" role="list">
        {steps.map((step, i) => {
          const Icon = resolveIcon(step.icon);
          const active = isActive(i);
          return (
            <li
              key={i}
              className="flex items-center gap-3"
              data-active={active ? 'true' : 'false'}
            >
              <div
                aria-hidden="true"
                className={
                  'w-10 h-10 rounded-full border flex items-center justify-center text-accent ' +
                  (active ? 'border-accent bg-accent/10' : 'border-dash-border bg-dash-surface/60')
                }
              >
                {Icon ? <Icon size={18} /> : <span className="font-mono text-xs">{i + 1}</span>}
              </div>
              <div className="text-xs">
                <div className="text-dash-primary font-medium">{step.label}</div>
                <div className="font-mono text-dash-muted">Step {i + 1}</div>
              </div>
              {i < steps.length - 1 && (
                <div aria-hidden="true" className="h-px w-6 bg-dash-border" />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
